'use client';

import { isSameDay, subDays } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { EarnHistoryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import { convertDate, getStartOfToday } from '@flipster/utils/datetime';
import Num, { formatToShortNumber, numberFormatter } from '@flipster/utils/num';

interface Props {
  data: EarnHistoryOfCurrency[];
}

interface ChartData {
  x: Date;
  y: number;
  isToday: boolean;
}

const chartFormatter = (n: number) => {
  if (n < 1000) {
    return numberFormatter(n, { withComma: true, precision: 2, toFixed: true });
  }
  return formatToShortNumber(n.toString());
};

export const Chart = ({ data }: Props) => {
  const t = useTranslations();
  const chartData = useMemo((): ChartData[] => {
    const startOfToday = getStartOfToday(true);

    // 7일간의 기본 데이터 생성 (과거 6일 + 오늘)
    const baseData = [
      { x: subDays(startOfToday, 6), isToday: false },
      { x: subDays(startOfToday, 5), isToday: false },
      { x: subDays(startOfToday, 4), isToday: false },
      { x: subDays(startOfToday, 3), isToday: false },
      { x: subDays(startOfToday, 2), isToday: false },
      { x: subDays(startOfToday, 1), isToday: false },
      { x: startOfToday, isToday: true },
    ];

    return baseData.map((item) => {
      const matchedItem = data.find((e) => {
        const date = convertDate(e.timestamp);
        if (!date) return false;
        return isSameDay(date, new Date(item.x));
      });

      return {
        ...item,
        // 숫자가 너무 작아서 비정상적으로 표시되는 것을 막기 위함
        y: Number(Num(matchedItem?.dailyPayoutAmount || 0).toFixed(2)),
      };
    });
  }, [data]);

  const maxValue = useMemo(() => {
    const max = Math.max(...chartData.map((item) => item.y));
    // max가 0이면 최소값 1을 사용하여 NaN 방지
    return Num(Math.max(max, 1)).multipliedBy(1.1).toNumber();
  }, [chartData]);

  // 차트 크기 설정
  const chartWidth = 320;
  const chartHeight = 180;
  const barWidth = 20;
  const barSpacing = (chartWidth - barWidth * 7) / 6; // 7개 바 사이의 6개 간격
  const paddingLeft = 0;
  const paddingTop = 20;
  const paddingBottom = 30;

  const getBarX = (index: number) => {
    return paddingLeft + index * (barWidth + barSpacing);
  };

  const getBarY = (value: number) => {
    const ratio = maxValue > 0 ? value / maxValue : 0;
    return paddingTop + (chartHeight - paddingTop - paddingBottom) * (1 - ratio);
  };

  const getBarHeight = (value: number) => {
    const ratio = maxValue > 0 ? value / maxValue : 0;
    return (chartHeight - paddingTop - paddingBottom) * ratio;
  };

  const getLabelY = (value: number) => {
    const barY = getBarY(value);
    return barY - 5; // 바 위쪽에 5px 간격
  };

  return (
    <div
      className={css({
        flex: 1,
        minW: 280,
        maxW: 378,
      })}
    >
      <svg
        width='100%'
        height={chartHeight}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className={css({
          display: 'block',
          overflow: 'visible',
        })}
      >
        {chartData.map((item, index) => {
          const x = getBarX(index);
          const y = getBarY(item.y);
          const height = getBarHeight(item.y);
          const labelY = getLabelY(item.y);

          return (
            <g key={index}>
              {/* 바 */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={height}
                rx={4}
                ry={4}
                fill={
                  item.isToday ? token('colors.content.default.level-5') : token('colors.background.default.level-4')
                }
              />

              {/* 데이터 라벨 */}
              <text
                x={x + barWidth / 2}
                y={labelY}
                textAnchor='middle'
                className={css({
                  fontSize: '12px',
                  fontWeight: 'normal',
                  fontFamily: 'var(--pp-neue-montreal)',
                  fill: item.isToday ? '#34C759' : '#EEEEEE',
                })}
              >
                {chartFormatter(item.y)}
              </text>
            </g>
          );
        })}

        {/* X축 라벨 */}
        {chartData.map((item, index) => {
          const x = getBarX(index) + barWidth / 2;
          const y = chartHeight - 5;

          return (
            <text
              key={`label-${index}`}
              x={x}
              y={y}
              textAnchor='middle'
              className={css({
                fontSize: '12px',
                fill: item.isToday ? '#EEEEEE' : '#8C8C8C',
              })}
            >
              {item.isToday
                ? t('Autogen_common.r251126.s_Today')
                : item.x.toLocaleDateString('en-US', { weekday: 'short' })}
            </text>
          );
        })}
      </svg>
    </div>
  );
};
